import { motion } from "framer-motion";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";

export default function CartDrawer({ open, onClose, cart, onUpdateQty, onRemove, onCheckout }) {
  const FREE_SHIPPING_THRESHOLD = 500;
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const freeShippingReached = total >= FREE_SHIPPING_THRESHOLD;
  const remainingForFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - total);
  const progress = Math.min((total / FREE_SHIPPING_THRESHOLD) * 100, 100);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#1A1A1A] border-l border-white/10 p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/5">
          <SheetTitle
            className="text-xl font-bold text-[#F5F5F0] flex items-center gap-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            <ShoppingBag size={20} className="text-[#D4AF37]" />
            Tu Carrito
          </SheetTitle>
          <SheetDescription className="text-sm text-[#A3A3A3]">
            {cart.length === 0
              ? "Tu carrito está vacío"
              : `${cart.reduce((s, i) => s + i.quantity, 0)} artículo${cart.length > 1 ? "s" : ""}`}
          </SheetDescription>
        </SheetHeader>

        {cart.length > 0 && (
          <div className="px-6 pt-4">
            <div className="rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-3 text-center text-xs text-[#F5F5F0]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#A3A3A3]">Envío gratis</span>
                <span className="text-[#D4AF37] font-semibold">
                  {freeShippingReached ? "100%" : `${Math.round(progress)}%`}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E5C158] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-2 text-[11px] text-[#A3A3A3]">
                {freeShippingReached
                  ? "¡Llegaste al envío gratis!"
                  : `Agrega ${remainingForFreeShipping.toFixed(0)} MXN más para activarlo`}
              </p>
            </div>
          </div>
        )}

        {/* Cart items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag size={48} className="text-white/10 mb-4" />
              <p className="text-[#A3A3A3] text-sm">
                Nada aquí todavía. Explora nuestra colección.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="flex gap-4 p-3 bg-[#262626] border border-white/5"
                  data-testid={`cart-item-${item.id}`}
                >
                  <div className="w-16 h-20 flex-shrink-0 bg-[#151515] overflow-hidden">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4
                          className="text-sm font-semibold text-[#F5F5F0] truncate"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {item.name}
                        </h4>
                        <p className="text-xs text-[#A3A3A3] mt-0.5">{item.weight}</p>
                      </div>
                      <button
                        onClick={() => onRemove(item.id)}
                        className="text-[#A3A3A3] hover:text-[#E07A5F] transition-colors p-1"
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-white/10">
                        <button
                          onClick={() => onUpdateQty(item.id, item.quantity - 1)}
                          className="p-1.5 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
                          data-testid={`qty-minus-${item.id}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-xs text-[#F5F5F0] font-medium min-w-[28px] text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => onUpdateQty(item.id, item.quantity + 1)}
                          className="p-1.5 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
                          data-testid={`qty-plus-${item.id}`}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <span className="text-sm font-bold text-[#D4AF37]">
                        ${(item.price * item.quantity).toFixed(0)} <span className="text-[10px] font-normal text-[#A3A3A3]">MXN</span>
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="px-6 py-5 border-t border-white/5 space-y-4">
            <div className="rounded-md border px-3 py-2 text-center text-xs">
              {freeShippingReached ? (
                <span className="text-[#4A7C59] font-semibold">¡Llegaste al envío gratis!</span>
              ) : (
                <span className="text-[#A3A3A3]">
                  Agrega <span className="text-[#D4AF37] font-semibold">${remainingForFreeShipping.toFixed(0)} MXN</span> para obtener envío gratis
                </span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#A3A3A3] uppercase tracking-wider">Total</span>
              <span
                className="text-2xl font-bold text-[#D4AF37]"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="cart-total"
              >
                ${total.toFixed(0)} <span className="text-sm font-normal text-[#A3A3A3]">MXN</span>
              </span>
            </div>
            <button
              data-testid="checkout-btn"
              onClick={onCheckout}
              className="w-full bg-[#D4AF37] text-[#1A1A1A] py-4 font-semibold uppercase tracking-wider text-sm hover:bg-[#E5C158] transition-all duration-300 gold-glow"
            >
              Proceder al Pago
            </button>
            <p className="text-[10px] text-center text-[#A3A3A3]">
              Pago seguro con Mercado Pago
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
